import type { PositionAxis } from '@felix/domain';
import { NumberInput, Select, Textarea, TextInput } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zonedStamp } from '../../inputs/lt-utc.js';
import { PortPicker } from '../../inputs/PortPicker.js';
import { PositionInput } from '../../inputs/PositionInput.js';
import { timeRefKey } from './form-errors.js';
import classes from './NoonReportScreen.module.css';
import { numberInputChange } from './number-input.js';

/**
 * Alanın hata kodu (`REQUIRED` …) çeviriye; kod `voyage.errors.<kod>`. Sıra hatası karşılaştırılan
 * zamanı adı ve yerel saatiyle yazar: "Must be after Cast Off (18.10.2026 14:20 LT)" (ortak geri
 * bildirimi 2026-10-19; `form-errors.ts`).
 */
export function useFieldError(name: string): string | undefined {
  const { t } = useTranslation();
  const { getFieldState, formState, getValues } = useFormContext();
  const error = getFieldState(name, formState).error;
  const code = error?.message;
  if (!code) return undefined;
  const after = typeof error.types?.after === 'string' ? error.types.after : null;
  const before = typeof error.types?.before === 'string' ? error.types.before : null;
  const ref = after ?? before;
  if (code === 'TIME_ORDER' && ref) {
    const offset = getValues('tzOffsetMin');
    const value = getValues(ref) as unknown;
    if (typeof value === 'string' && value) {
      return t(`voyage.errors.TIME_ORDER_${after ? 'AFTER' : 'BEFORE'}`, {
        ref: t(`voyage.errors.timeRefs.${timeRefKey(ref, getValues('kind'))}`, {
          defaultValue: ref,
        }),
        time: zonedStamp(value, typeof offset === 'number' ? offset : 0),
      });
    }
  }
  return t(`voyage.errors.${code}`, { defaultValue: t('voyage.errors.GENERIC') });
}

const cx = (...names: (string | false | null | undefined)[]) => names.filter(Boolean).join(' ');

/**
 * Alan görünümü (M6f G4-11, G4-12, G4-30, G4-31):
 * - `stacked`: etiket üstte (varsayılan);
 * - `inline`: etiket solda, değer kutusu sağda (Speed Calculation, sefer-noon-report-makine.jpeg);
 * - `cell`: tablo hücresinde çerçevesiz giriş, odakta halka (Auxiliary Machinery).
 */
export type FieldLayout = 'stacked' | 'inline' | 'cell';

interface FieldProps {
  name: string;
  label: string;
  required?: boolean;
  readOnly?: boolean;
  /** Etiketsiz tablo hücresi için ekran okuyucu adı. */
  ariaLabel?: string;
  layout?: FieldLayout;
}

/**
 * Ortak sınıflar. Salt okunur (gönderilmiş, kilitli rapor ya da ofis) alan dolu ve soluk zeminde;
 * zorunluluk yıldızı yalnız düzenlenirken (G4-12).
 */
function fieldClasses(layout: FieldLayout, readOnly: boolean) {
  return {
    root: cx(layout === 'inline' && classes.inlineRoot) || undefined,
    input: cx(
      classes.input,
      layout === 'cell' && classes.cellInput,
      readOnly && layout !== 'cell' && classes.locked,
    ),
    label: classes.label ?? '',
    required: classes.required ?? '',
    section: classes.section ?? '',
  };
}

function labelProps(
  label: string,
  ariaLabel: string | undefined,
  layout: FieldLayout,
  hideLabel: boolean | undefined,
) {
  return hideLabel || layout === 'cell'
    ? { 'aria-label': ariaLabel ?? label }
    : { label, labelProps: { title: label } };
}

/** Sayı alanı: boş değer `null` olarak saklanır (gövdeye girmez). */
export function NumberField({
  name,
  label,
  required,
  readOnly,
  ariaLabel,
  unit,
  decimals = 2,
  fixed = false,
  hideLabel,
  layout = 'stacked',
  placeholder,
}: FieldProps & {
  unit?: string;
  decimals?: number;
  /** Ondalık sabit ("6.80"; Machinery pervane ve hız alanları, G4-05). */
  fixed?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
}) {
  const { field } = useController({ name });
  const error = useFieldError(name);
  const locked = !!readOnly;
  // Yazarken ara durum ("14.", "16.90") ekranda kalır; değer dışarıdan değişirse (form sıfırlanınca)
  // saklanan sayı gösterilir
  const [text, setText] = useState<string | null>(null);
  const stored = typeof field.value === 'number' ? field.value : null;
  const shown = text !== null && numberInputChange(text).stored === stored ? text : (stored ?? '');
  return (
    <NumberInput
      {...labelProps(label, ariaLabel, layout, hideLabel)}
      withAsterisk={!!required && !hideLabel && !locked && layout !== 'cell'}
      required={!!required}
      readOnly={locked}
      variant={layout === 'cell' ? 'unstyled' : 'default'}
      hideControls
      decimalScale={decimals}
      // Sabit ondalık yalnız gösterimde: yazarken sıfır eklemek girişi bozar ("16.90" → boş)
      fixedDecimalScale={fixed && locked && decimals > 0}
      allowNegative
      {...(placeholder ? { placeholder } : layout === 'cell' ? { placeholder: '–' } : {})}
      value={shown}
      onChange={(value) => {
        const change = numberInputChange(value);
        setText(change.text);
        field.onChange(change.stored);
      }}
      onBlur={field.onBlur}
      ref={field.ref}
      {...(unit
        ? { rightSection: <span className={classes.unit}>{unit}</span>, rightSectionWidth: 44 }
        : {})}
      error={error ?? null}
      classNames={fieldClasses(layout, locked)}
    />
  );
}

export function TextField({
  name,
  label,
  required,
  readOnly,
  placeholder,
  leftSection,
  hideLabel,
  ariaLabel,
  layout = 'stacked',
}: FieldProps & { placeholder?: string; leftSection?: ReactNode; hideLabel?: boolean }) {
  const { field } = useController({ name });
  const error = useFieldError(name);
  const locked = !!readOnly;
  return (
    <TextInput
      {...labelProps(label, ariaLabel, layout, hideLabel)}
      withAsterisk={!!required && !hideLabel && !locked && layout !== 'cell'}
      required={!!required}
      readOnly={locked}
      variant={layout === 'cell' ? 'unstyled' : 'default'}
      {...(placeholder ? { placeholder } : layout === 'cell' ? { placeholder: '–' } : {})}
      {...(leftSection ? { leftSection } : {})}
      value={typeof field.value === 'string' ? field.value : ''}
      onChange={(event) => field.onChange(event.currentTarget.value)}
      onBlur={field.onBlur}
      ref={field.ref}
      error={error ?? null}
      classNames={fieldClasses(layout, locked)}
    />
  );
}

/**
 * Konum alanı (ADR 0054 §3): derece, dakika ve yön kutuları; form değeri kanonik metin
 * (`34° 45.6' N`), gönderimde `parsePosition` ile ondalık dereceye.
 */
export function PositionField({
  name,
  axis,
  label,
  required,
  readOnly,
  leftSection,
  className,
}: FieldProps & { axis: PositionAxis; leftSection?: ReactNode; className?: string | undefined }) {
  const { field } = useController({ name });
  const error = useFieldError(name);
  const locked = !!readOnly;
  const base = fieldClasses('stacked', locked);
  return (
    <PositionInput
      axis={axis}
      label={label}
      required={!!required}
      readOnly={locked}
      value={typeof field.value === 'string' ? field.value : ''}
      onChange={field.onChange}
      onBlur={field.onBlur}
      error={error ?? null}
      {...(leftSection ? { leftSection } : {})}
      {...(className ? { className } : {})}
      classNames={{ label: base.label, input: base.input, required: base.required }}
    />
  );
}

/**
 * Rapordaki liman (ADR 0048 §6): ad `data.port`, listedeki kod `data.portCode`. Listede yoksa
 * metin kalır, kod yazılmaz. Salt okunurda düz metin alanı.
 */
export function PortField({
  name,
  codeName,
  label,
  required,
  readOnly,
}: FieldProps & { codeName: string }) {
  const { field } = useController({ name });
  const { field: code } = useController({ name: codeName });
  const nameError = useFieldError(name);
  const codeError = useFieldError(codeName);
  const error = nameError ?? codeError;
  if (readOnly) {
    return <TextField name={name} label={label} required={!!required} readOnly />;
  }
  return (
    <PortPicker
      label={label}
      withAsterisk={!!required}
      value={{
        name: typeof field.value === 'string' ? field.value : '',
        code: typeof code.value === 'string' ? code.value : null,
      }}
      onChange={(port) => {
        field.onChange(port.name);
        code.onChange(port.code ?? undefined);
      }}
      error={error ?? null}
      classNames={{ label: classes.label ?? '' }}
    />
  );
}

export function SelectField({
  name,
  label,
  required,
  readOnly,
  options,
  hideLabel,
  ariaLabel,
  layout = 'stacked',
}: FieldProps & { options: { value: string; label: string }[]; hideLabel?: boolean }) {
  const { field } = useController({ name });
  const error = useFieldError(name);
  const locked = !!readOnly;
  return (
    <Select
      {...labelProps(label, ariaLabel, layout, hideLabel)}
      withAsterisk={!!required && !hideLabel && !locked && layout !== 'cell'}
      required={!!required}
      readOnly={locked}
      variant={layout === 'cell' ? 'unstyled' : 'default'}
      data={options}
      value={typeof field.value === 'string' ? field.value : null}
      onChange={(value) => field.onChange(value)}
      onBlur={field.onBlur}
      ref={field.ref}
      error={error ?? null}
      allowDeselect={false}
      // Seçim temizlenmez (× yok); salt okunurda ok da yok (G4-11, G4-12)
      clearable={false}
      {...(locked ? { rightSection: null } : {})}
      {...(layout === 'cell' ? { placeholder: '–' } : {})}
      classNames={fieldClasses(layout, locked)}
    />
  );
}

/**
 * Not alanı: sayaçlı (0/500, ekrandaki gibi). Salt okunurda sayaç yok; boş not "No remarks".
 */
export function NoteField({
  name,
  label,
  readOnly,
  placeholder,
  minRows = 3,
}: FieldProps & { placeholder?: string; minRows?: number }) {
  const { t } = useTranslation();
  const { field } = useController({ name });
  const error = useFieldError(name);
  const value = typeof field.value === 'string' ? field.value : '';
  if (readOnly && value.trim() === '') {
    return (
      <p className={classes.noRemarks} role="note" aria-label={label}>
        {t('voyage.noon.noRemarks')}
      </p>
    );
  }
  return (
    <div className={classes.note}>
      <Textarea
        aria-label={label}
        readOnly={!!readOnly}
        {...(placeholder && !readOnly ? { placeholder } : {})}
        autosize
        minRows={minRows}
        maxLength={500}
        value={value}
        onChange={(event) => field.onChange(event.currentTarget.value)}
        onBlur={field.onBlur}
        ref={field.ref}
        error={error ?? null}
        classNames={{
          input: cx(classes.input, classes.noteInput, readOnly && classes.locked),
          label: classes.label ?? '',
        }}
      />
      {readOnly ? null : <span className={classes.counter}>{`${value.length}/500`}</span>}
    </div>
  );
}

/** Hesaplanan alan: yalnız gösterilir (sunucu aynı hesabı domain'den yapar). */
export function ComputedField({
  label,
  value,
  required,
  layout = 'stacked',
  rightSection,
}: {
  label: string;
  value: string;
  required?: boolean;
  layout?: Exclude<FieldLayout, 'cell'>;
  rightSection?: ReactNode;
}) {
  return (
    <TextInput
      label={label}
      withAsterisk={!!required}
      readOnly
      value={value}
      {...(rightSection ? { rightSection } : {})}
      classNames={{
        root: cx(layout === 'inline' && classes.inlineRoot) || undefined,
        input: classes.computed ?? '',
        label: classes.label ?? '',
        required: classes.required ?? '',
      }}
    />
  );
}

/** Salt okunur bilgi alanı (önceki rapor zamanı): normal giriş görünümü, kalın değil (G4-17). */
export function InfoField({
  label,
  value,
  leftSection,
  rightSection,
}: {
  label: string;
  value: string;
  leftSection?: ReactNode;
  rightSection?: ReactNode;
}) {
  return (
    <TextInput
      label={label}
      readOnly
      value={value}
      {...(leftSection ? { leftSection } : {})}
      {...(rightSection ? { rightSection, rightSectionPointerEvents: 'none' as const } : {})}
      classNames={{ input: cx(classes.input, classes.locked), label: classes.label ?? '' }}
    />
  );
}

/**
 * Tablo bölgesi: kenarlıklı kap ve başlık bandı (plan §0.6 "Tablo dili"); dar ekranda yatay kayar
 * ve klavyeyle de kaydırılır (WCAG 2.1.1; axe scrollable-region).
 */
export function ScrollTable({
  label,
  children,
  framed = true,
}: {
  label: string;
  children: ReactNode;
  /** Kenarlıklı kap (varsayılan); kartın içinde zaten çerçeve varsa `false`. */
  framed?: boolean;
}) {
  return (
    <section
      className={classes.tableWrap}
      data-framed={framed || undefined}
      aria-label={label}
      // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge
      tabIndex={0}
    >
      {children}
    </section>
  );
}
